import { createSlice } from '@reduxjs/toolkit'
import { supabase } from '../../lib/supabaseClient'
import { fetchCart, mergeGuestCartWithUserCart, clearCart, setUserId } from './cartSlice'

const initialState = {
  user: null,
  session: null,
  loading: false,
  error: null,
  isAdmin: false,
  userProfile: null
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthLoading: (state, action) => {
      state.loading = action.payload
    },
    setUser: (state, action) => {
      state.user = action.payload
      // Store userId in localStorage when setting user
      if (action.payload?.id) {
        localStorage.setItem('userId', action.payload.id)
      } else {
        localStorage.removeItem('userId')
      }
    },
    setSession: (state, action) => {
      state.session = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    logout: (state) => {
      state.user = null
      state.session = null
      state.error = null
      state.isAdmin = false
      state.userProfile = null
      // Clear userId from localStorage on logout
      localStorage.removeItem('userId')
    },
    setIsAdmin: (state, action) => {
      state.isAdmin = action.payload
    },
    setUserProfile: (state, action) => {
      state.userProfile = action.payload
    }
  },
})

// Action creators
export const { 
  setAuthLoading, 
  setUser, 
  setSession, 
  setError, 
  clearError, 
  logout,
  setIsAdmin,
  setUserProfile
} = authSlice.actions

// Helper function to get the redirect URL based on the environment
const getRedirectURL = () => {
  // Get the site URL from environment variable or fallback to window.location.origin
  const siteURL = import.meta.env.VITE_SITE_URL || window.location.origin;
  
  // The path for the callback
  const callbackPath = '/auth/callback';
  
  // Construct the full redirect URL
  const redirectURL = `${siteURL}${callbackPath}`;
  
  // Log the redirect URL for debugging
  console.log('Auth redirect URL:', redirectURL);
  
  return redirectURL;
};

// Async thunks
export const signIn = ({ email, password }) => async (dispatch) => {
  try {
    dispatch(setAuthLoading(true))
    dispatch(clearError())
    
    if (!email || !password) {
      throw new Error('Email and password are required')
    }
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    
    if (error) throw error
    
    if (!data || !data.user) {
      throw new Error('Authentication failed. Please try again.')
    }
    
    dispatch(setUser(data.user))
    dispatch(setSession(data.session))
    
    // Fetch user profile to check if admin
    await dispatch(fetchUserProfile(data.user.id))
    
    // Load and merge cart data
    await dispatch(mergeGuestCartWithUserCart(data.user.id))
    
    return data // Return data for the .then() handler
  } catch (error) {
    // Provide more user-friendly error messages
    let errorMessage = error.message
    if (error.message.includes('Invalid login credentials')) {
      errorMessage = 'Invalid email or password. Please try again.'
    } else if (error.message.includes('Email not confirmed')) {
      errorMessage = 'Please confirm your email address before logging in.'
    }
    
    dispatch(setError(errorMessage))
    throw error // Re-throw to trigger the .catch() handler
  } finally {
    dispatch(setAuthLoading(false))
  }
}

export const signUp = ({ email, password, name, isAdmin = false }) => async (dispatch) => {
  try {
    dispatch(setAuthLoading(true))
    dispatch(clearError())
    
    if (!email || !password || !name) {
      throw new Error('Name, email, and password are required')
    }
    
    if (password.length < 6) {
      throw new Error('Password must be at least 6 characters')
    }
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name,
          is_admin: isAdmin
        },
      },
    })
    
    if (error) throw error
    
    if (!data) {
      throw new Error('Registration failed. Please try again.')
    }
    
    // Check if email confirmation is required
    if (data.user && data.user.identities && data.user.identities.length === 0) {
      dispatch(setError('This email is already registered. Please log in instead.'))
      return null
    }
    
    if (data.user && !data.session) {
      // Email confirmation is required
      dispatch(setError('Please check your email to confirm your account before logging in.'))
      return { requiresEmailConfirmation: true }
    }
    
    dispatch(setUser(data.user))
    dispatch(setSession(data.session))
    
    // Fetch user profile to check if admin
    if (data.user) {
      await dispatch(fetchUserProfile(data.user.id))
    }
    
    return data
  } catch (error) {
    let errorMessage = error.message
    if (error.message.includes('already registered')) {
      errorMessage = 'This email is already registered. Please log in instead.'
    }
    
    dispatch(setError(errorMessage))
    throw error
  } finally {
    dispatch(setAuthLoading(false))
  }
}

export const signOut = () => async (dispatch) => {
  try {
    dispatch(setAuthLoading(true))
    
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    
    // Clear the cart from store and storage
    dispatch(clearCart())
    
    // Clear all auth state
    dispatch(logout())
    
    return true
  } catch (error) {
    dispatch(setError(error.message))
    throw error
  } finally {
    dispatch(setAuthLoading(false))
  }
}

export const getSession = () => async (dispatch) => {
  try {
    dispatch(setAuthLoading(true))
    const { data, error } = await supabase.auth.getSession()
    if (error) throw error
    
    if (data && data.session) {
      dispatch(setUser(data.session.user))
      dispatch(setSession(data.session))
      
      // Fetch user profile to check if admin
      await dispatch(fetchUserProfile(data.session.user.id))
      
      // Load user's cart from Supabase
      await dispatch(setUserId(data.session.user.id))
      await dispatch(fetchCart(data.session.user.id))
      
      return data
    } else {
      // No active session found
      dispatch(logout())
      return null
    }
  } catch (error) {
    dispatch(setError(error.message))
    dispatch(logout())
    throw error
  } finally {
    dispatch(setAuthLoading(false))
  }
}

export const fetchUserProfile = (userId) => async (dispatch) => {
  try {
    if (!userId) return null
    
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single()
    
    if (error) throw error
    
    dispatch(setUserProfile(data))
    dispatch(setIsAdmin(data.is_admin || false))
    
    return data
  } catch (error) {
    console.error('Error fetching user profile:', error)
    return null
  }
}

export const createAdminUser = ({ email, password, name }) => async (dispatch) => {
  try {
    dispatch(setAuthLoading(true))
    dispatch(clearError())
    
    // First, create the user
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name,
          is_admin: true
        },
      },
    })
    
    if (error) throw error
    
    if (!data || !data.user) {
      throw new Error('Failed to create admin user')
    }
    
    // Update the user profile to set is_admin to true
    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({ is_admin: true })
      .eq('id', data.user.id)
    
    if (updateError) throw updateError
    
    return { success: true, user: data.user }
  } catch (error) {
    dispatch(setError(error.message))
    throw error
  } finally {
    dispatch(setAuthLoading(false))
  }
}

export const signInWithGoogle = () => async (dispatch) => {
  try {
    dispatch(setAuthLoading(true));
    dispatch(clearError());
    
    // Get the redirect URL using the helper function
    const redirectURL = getRedirectURL();
    
    console.log('Starting Google OAuth login with redirect to:', redirectURL);
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectURL,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      }
    });
    
    if (error) {
      console.error('Google OAuth error:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Failed to sign in with Google:', error);
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setAuthLoading(false));
  }
};

export default authSlice.reducer